import React, { useState, useRef, useEffect } from 'react';
import InputMask from 'react-input-mask';

const countries = [
    { code: '+7', name: 'KZ', flag: '🇰🇿', mask: '+7 (999) 999 99 99', country: 'Kazakhstan' },
    { code: '+7', name: 'RU', flag: '🇷🇺', mask: '+7 (999) 999 99 99', country: 'Russia' },
    { code: '+86', name: 'CN', flag: '🇨🇳', mask: '+86 (999) 9999 9999', country: 'China' },
    { code: '+998', name: 'UZ', flag: '🇺🇿', mask: '+998 (99) 999 99 99', country: 'Uzbekistan' },
    { code: '+996', name: 'KG', flag: '🇰🇬', mask: '+996 (999) 999 999', country: 'Kyrgyzstan' },
];

const PhoneInput = ({ label, value, onChange, onCountryChange }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(countries[0]);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const inputId = `phone-input-${Math.random().toString(36).substr(2, 9)}`;

    const setInputRef = (element) => {
        inputRef.current = element;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const handleFocus = () => {
        setIsFocused(true);
        setIsDropdownOpen(false);
    };

    const handleBlur = () => {
        blurTimeoutRef.current = setTimeout(() => {
            if (!isDropdownOpen) {
                setIsFocused(false);
            }
        }, 200);
    };

    const handleCountrySelect = (country) => {
        setSelectedCountry(country);
        setIsDropdownOpen(false);
        if (onCountryChange) {
            onCountryChange(country);
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handlePhoneChange = (e) => {
        onChange(e);
    };

    const phoneValue = value || '';
    const hasValue = phoneValue.replace(/\D/g, '').length > 0;

    return (
        <div className="relative w-full">
            {/* Label */}
            {label && (
                <label
                    htmlFor={inputId}
                    className={`block text-sm font-semibold mb-2 ${isFocused ? 'text-[#147F26]' : 'text-gray-600'}`}
                >
                    {label}
                </label>
            )}

            {/* Phone Input Container */}
            <div className="relative">
                {/* Main Input Field */}
                <div
                    className={`relative flex bg-white border-2 rounded-2xl transition-all duration-300 ${
                        isFocused
                            ? 'border-[#147F26] ring-4 ring-[#147F26]/20 shadow-xl shadow-[#147F26]/10'
                            : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                    {/* Country Dropdown Button */}
                    <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={`flex items-center gap-2 px-4 h-14 border-r-2 transition-all duration-200 ${
                            isDropdownOpen
                                ? 'border-[#147F26] bg-[#147F26]/10'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <span className="text-2xl leading-none transition-transform duration-200 hover:scale-110">
                            {selectedCountry.flag}
                        </span>
                        <span className={`font-bold text-sm ${
                            isDropdownOpen ? 'text-[#147F26]' : 'text-gray-700'
                        }`}>
                            {selectedCountry.code}
                        </span>
                        <svg
                            className={`w-4 h-4 text-gray-400 transition-all duration-200 ${isDropdownOpen ? 'rotate-180 text-[#147F26]' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Phone Input */}
                    <div className="flex-1 relative">
                        <InputMask
                            id={inputId}
                            mask={selectedCountry.mask.replace(selectedCountry.code, '').trim()}
                            value={phoneValue}
                            onChange={handlePhoneChange}
                            className="w-full h-14 py-3 px-5 text-gray-800 text-lg font-medium bg-transparent outline-none placeholder:text-gray-300"
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            inputRef={setInputRef}
                            placeholder="XXX XXX XX XX"
                        />
                    </div>

                    {/* Status Icon */}
                    <div className="flex items-center px-4">
                        {hasValue && !isFocused && (
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        )}
                        {isFocused && (
                            <div className="w-6 h-6 rounded-full bg-[#147F26] flex items-center justify-center animate-pulse">
                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                    </div>
                </div>

                {/* Dropdown - OUTSIDE the input container to avoid overflow clipping */}
                {isDropdownOpen && (
                    <div
                        ref={dropdownRef}
                        className="absolute top-full left-0 mt-2 w-64 bg-white border-2 border-[#147F26] rounded-2xl shadow-2xl z-50 overflow-hidden"
                    >
                        <div className="p-2">
                            {countries.map((country, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleCountrySelect(country)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-150 ${
                                        selectedCountry.code === country.code && selectedCountry.name === country.name
                                            ? 'bg-[#147F26] text-white'
                                            : 'hover:bg-[#147F26]/10'
                                    } ${index !== countries.length - 1 ? 'mb-1' : ''}`}
                                >
                                    <span className="text-2xl leading-none">{country.flag}</span>
                                    <span className={`font-semibold ${
                                        selectedCountry.code === country.code && selectedCountry.name === country.name
                                            ? 'text-white'
                                            : 'text-gray-800'
                                    }`}>
                                        {country.code}
                                    </span>
                                    <span className={`text-xs ${
                                        selectedCountry.code === country.code && selectedCountry.name === country.name
                                            ? 'text-white/80'
                                            : 'text-gray-400'
                                    } ml-auto`}>
                                        {country.name}
                                    </span>
                                    {selectedCountry.code === country.code && selectedCountry.name === country.name && (
                                        <svg className="w-5 h-5 text-white ml-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Helper Text */}
            {hasValue && !isFocused && (
                <p className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Номер дұрыс енгізілді
                </p>
            )}
        </div>
    );
};

export default PhoneInput;
